import React, { lazy, Suspense } from 'react'
import { gql } from '@apollo/client'
import { ApolloProvider, useQuery } from '@apollo/client/react'
import { I18nextProvider } from 'react-i18next'
import i18n from '../i18n'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { apolloClient } from '../apollo'
import { lightTheme } from '../styles/theme'
import AppRouter from './AppRouter'
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { BrowserRouter } from 'react-router-dom'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DocumentTitleContextProvider } from '../utils/providers/documentTitleContext'
import ReactHelmet from '../components/head/ReactHelmet'
import { SnackbarContextProvider } from '../utils/providers/snackbarContext'
import Snackbar from '../components/layout/Snackbar'
import { ErrorBoundary } from '../components/error/ErrorBoundary'
import PwsSpinner from '../components/loader/PwsSpinner'
import OldFrontRedirection from './OldFrontRedirection'
import { getIsOldFrontUrl } from '../utils/isOldFrontUrl'

const LoggedOutRouter = lazy(() => import('./LoggedOutRouter'))

const IsLoggedIn = (): JSX.Element => {
  const IS_LOGGED_IN = gql`
    query IsUserLoggedIn {
      isLoggedIn @client
    }
  `

  const { data, loading } = useQuery(IS_LOGGED_IN)

  if (loading || data === undefined) return <PwsSpinner />
  if (data.isLoggedIn) return <AppRouter />
  return <LoggedOutRouter />
}

export const App = (): JSX.Element => {
  const isOldFrontUrl = getIsOldFrontUrl()

  return (
    <I18nextProvider i18n={i18n}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ApolloProvider client={apolloClient}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={lightTheme}>
              <CssBaseline />
              <ErrorBoundary>
                <DocumentTitleContextProvider>
                  <SnackbarContextProvider>
                    <Suspense
                      fallback={<PwsSpinner />}
                      /**
                       * Will be used when Concurrent mode is release
                       * https://github.com/facebook/react/issues/13206
                       * maxDuration={1000}
                       */
                    >
                      <ReactHelmet />
                      <BrowserRouter>{isOldFrontUrl ? <OldFrontRedirection /> : <IsLoggedIn />}</BrowserRouter>
                      <Snackbar />
                    </Suspense>
                  </SnackbarContextProvider>
                </DocumentTitleContextProvider>
              </ErrorBoundary>
            </ThemeProvider>
          </StyledEngineProvider>
        </ApolloProvider>
      </LocalizationProvider>
    </I18nextProvider>
  )
}
